import { AsyncPipe } from '@angular/common';
import { Component, computed, inject, input, viewChild } from '@angular/core';
import { rxResource, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { KENDO_CARD, KENDO_GRIDLAYOUT, KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { AbstractManageComponent } from '@unity/abstract/abstract-manage.component';
import { RoleDetailedDto, RolesClient, RoleUpdateDto } from '@unity/clients';
import { DashboardPermissionsSharedComponent } from '@unity/components/shared/dashboard-permissions-shared/dashboard-permissions-shared.component';
import { GridUserSharedComponent } from '@unity/components/shared/grid-user-shared/grid-user-shared.component';
import { AuthorizeDirective } from '@unity/directives/authorize.directive';
import { TextareaControlComponent } from '@unity/forms/textarea-control/textarea-control.component';
import { TextboxControlComponent } from '@unity/forms/textbox-control/textbox-control.component';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { SortPipe } from '@unity/pipes/sort.pipe';
import { ActionDeleteSharedComponent } from '@unity/shared/composed/action-delete-shared/action-delete-shared.component';
import { PanelManageSharedComponent } from '@unity/shared/composed/panel-manage-shared/panel-manage-shared.component';
import { GridcellClickableSharedComponent } from '@unity/shared/gridcell-clickable-shared/gridcell-clickable-shared.component';
import { stringFormControl } from '@unity/utils/form-controls';
import { FormEditState, FormGroupControlSchema, UnityEntitlementPath, UnityFeatureTypeStrings } from '@unity/utils/types';
import { EMPTY, filter, switchMap } from 'rxjs';

class RoleUpdateSchema implements FormGroupControlSchema<RoleUpdateDto> {
  public name: FormControl<string>;
  public description: FormControl<string>;

  public constructor(role?: RoleDetailedDto) {
    this.name = stringFormControl({ disabled: true, value: role?.name });
    this.description = stringFormControl({ disabled: true, value: role?.description });
  }
}

@Component({
  selector: 'unity-manage-role-page',
  standalone: true,
  imports: [
    KENDO_CARD,
    KENDO_GRIDLAYOUT,
    KENDO_STACKLAYOUT,
    KENDO_LABEL,
    KENDO_BUTTON,
    ReactiveFormsModule,
    AsyncPipe,
    SortPipe,
    PageContainerComponent,
    GridcellClickableSharedComponent,
    TextboxControlComponent,
    TextareaControlComponent,
    GridUserSharedComponent,
    DashboardPermissionsSharedComponent,
    PanelManageSharedComponent,
    AuthorizeDirective,
    ActionDeleteSharedComponent,
  ],
  templateUrl: './manage-role-page.component.html',
  styleUrl: './manage-role-page.component.scss',
})
export class ManageRolePageComponent extends AbstractManageComponent<RoleDetailedDto, RoleUpdateDto, RoleUpdateSchema> {
  private readonly rolesClient = inject(RolesClient);

  public readonly $roleId = input.required({ alias: 'roleId', transform: (value: string) => parseInt(value) });

  private readonly $dashboardPermissionsReference = viewChild<DashboardPermissionsSharedComponent>('dashboardPermissions');

  protected override mainResource = rxResource({
    params: () => {
      const resourceId = this.$resourceId();
      return resourceId ? { resourceId } : undefined;
    },
    stream: ({ params: { resourceId } }) => this.rolesClient.getOneAsDetailed(resourceId),
  });

  protected override updateResourceFn$ = (resourceId: number, form: FormGroup<RoleUpdateSchema>) =>
    this.rolesClient.updateOne(resourceId, form.getRawValue());

  protected override deleteResourceFn$ = (resourceId: number) => this.rolesClient.deleteOne(resourceId);

  protected override availableTransitionsFn$ = (_resourceId: number) => EMPTY;
  protected override doTransitionFn$ = (_resourceId: number, _transition: unknown) => EMPTY;

  protected override newControlsFn(dto?: RoleDetailedDto) {
    return new RoleUpdateSchema(dto);
  }

  protected override editEntitlement: UnityEntitlementPath = 'Role:Update';
  protected override transitionEntitlement: UnityEntitlementPath = 'Role:Transition';
  protected override deleteEntitlement: UnityEntitlementPath = 'Role:Delete';
  protected override resourceName = 'Role';
  protected override $resourceId = this.$roleId;
  protected override readonly $contentHeader = computed(() => {
    const role = this.$resource();
    return `Agency: ${role?.agency?.name}`;
  });
  protected readonly $contentHeaderSecondary = computed(() => {
    const role = this.$resource();
    return `Role: ${role?.name}`;
  });

  private role$ = toObservable(this.$resource).pipe(filter(role => !!role));

  protected childRoles$ = this.role$.pipe(switchMap(res => this.rolesClient.getAllChildrenForRoleAsDetailed(res.id)));

  protected readonly ROLE_MANAGEMENT_FEATURE_TYPE: UnityFeatureTypeStrings = 'RoleManagement';

  protected readonly $cols = computed(() => (this.layoutService.$orientation() == 'horizontal' ? ['auto', 'auto'] : ['auto']));
  protected readonly $rows = computed(() => (this.layoutService.$orientation() == 'horizontal' ? ['auto'] : ['auto', 'auto']));

  protected onEditChange(event: FormEditState) {
    // TODO refactor this to not depend on another components public methods.
    this.$dashboardPermissionsReference()?.onEdit(event.editing);
    this.toggleWorkflowActions(event);
  }

  protected override onSave() {
    // TODO refactor this to not depend on another components public methods.
    this.$dashboardPermissionsReference()?.onSave();
    super.onSave();
  }

  protected onPermissionTouched() {
    const formGroup = this.$formGroup();
    formGroup.markAsDirty();
  }

  protected childDisplayName(childRole: RoleDetailedDto) {
    if (childRole.agency.id !== this.$resource()?.agency.id) {
      return `${childRole.name} (${childRole.agency.name})`;
    }
    return childRole.name;
  }
}
